import { useMemo } from 'react';
import { useLocation } from 'react-router';
import {
    DirectoryFilterSidebar,
    type CategoryItem,
} from '@ui-rocco/pages/directory-browse/DirectoryFilterSidebar';
import {
    DirectoryBrowseThemeProvider,
    type DirectoryBrowseTheme,
} from '@ui-rocco/pages/directory-browse/directory-browse-theme';
import { useProfessionalTabFilterSort } from '@modules/professional/hook/use-professional-tab-filter-sort';
import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';
import type {
    FilterStates,
    FilterStatesKey,
} from '@modules-rocco/filters/types/filter-states';
import type {
    CategoryFilterState,
    CategoryTree,
} from '@modules-rocco/filters/filter-state/category-filter-state';
import type { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import type { SearchServiceQueryFilters } from '@modules-rocco/search/types/api';

interface ProfessionalTabFilterSidebarProps {
    searchIndex: SearchIndexes;
    config: FiltersConfig;
    /** The tab loader's `filterStatesRocco`. */
    initialFilterStates: FilterStates;
    theme?: DirectoryBrowseTheme;
    extraIgnoreFilterKeys?: FilterStatesKey[];
    defaultViewProjectsBy?: string;
    /** The tab loader's `aggregateScopeFilters` (ID-based scopes). */
    aggregateScopeFilters?: SearchServiceQueryFilters;
}

const findParent = (
    root: CategoryTree,
    target: CategoryTree,
): CategoryTree | null => {
    if (root.children.some((child) => child.url === target.url)) {
        return root;
    }
    for (const child of root.children) {
        const found = findParent(child, target);
        if (found) {
            return found;
        }
    }
    return null;
};

/**
 * One drill-down level of the professional-scoped category tree: the selected
 * node's children (or its siblings at a leaf), plus an "All" reset once a
 * category is applied. Links stay on the tab pathname with `categoryLink`
 * query params, keeping the other applied filters intact.
 */
const buildCategoryItems = (
    state: CategoryFilterState,
    pathname: string,
    search: string,
): CategoryItem[] => {
    const selected =
        !state.isRoot && state.selectedCategory ? state.selectedCategory : null;
    const listParent = selected
        ? selected.children.length
            ? selected
            : (findParent(state.category, selected) ?? state.category)
        : state.category;

    if (!listParent.children.length) {
        return [];
    }

    const linkFor = (categoryLink: string | null) => {
        const params = new URLSearchParams(search);
        params.delete('page');
        if (categoryLink) {
            params.set('categoryLink', categoryLink);
        } else {
            params.delete('categoryLink');
        }
        const query = params.toString();
        return `${pathname}${query ? `?${query}` : ''}#sections`;
    };

    const items: CategoryItem[] = listParent.children.map((child) => ({
        id: child.url,
        title: child.title,
        link: linkFor(child.url),
        isActive: child.url === selected?.url,
    }));

    if (selected) {
        items.unshift({
            id: state.category.url || 'all',
            title: 'All',
            link: linkFor(null),
            isActive: false,
        });
    }

    return items;
};

/**
 * The Rocco filter sidebar wired for a professional list tab: the tab's own
 * filter config and URL format, themed via the directory browse theme (the
 * case-studies tab is dark on desktop).
 */
export const ProfessionalTabFilterSidebar = ({
    searchIndex,
    config,
    initialFilterStates,
    theme = 'light',
    extraIgnoreFilterKeys,
    defaultViewProjectsBy,
    aggregateScopeFilters,
}: ProfessionalTabFilterSidebarProps) => {
    const location = useLocation();

    const { searchFilters, sortOptions } = useProfessionalTabFilterSort({
        config,
        searchIndex,
        initialFilterStates,
        extraIgnoreFilterKeys,
        defaultViewProjectsBy,
        aggregateScopeFilters,
    });

    const categoryState = initialFilterStates.states.category;
    const categories = useMemo(
        () =>
            categoryState
                ? buildCategoryItems(
                      categoryState,
                      location.pathname,
                      location.search,
                  )
                : undefined,
        [categoryState, location.pathname, location.search],
    );

    return (
        <DirectoryBrowseThemeProvider value={theme}>
            <DirectoryFilterSidebar
                searchFilters={searchFilters}
                sortOptions={sortOptions}
                categories={categories}
                hiddenSectionTitles={[]}
                // The tab layout owns the page gutter, so drop the browse
                // aside's offset.
                className="md:ml-0"
            />
        </DirectoryBrowseThemeProvider>
    );
};
